import React from 'react';
import { Descriptions, Tag } from 'antd';
import styled from 'styled-components';
import OpenCouponDetail from '../../../../../components/link/OpenCouponDetail';
import OpenEventDetail from '../../../../../components/link/OpenEventDetail';
import { PromotionSchedule } from '../../../../../lib/api/promotionSchedule';
import { COUPON_DISCOUNT_TYPE, STATUS_LABEL, TRACK_META, TYPE_LABEL } from '../../constants';
import { getDayCount, getTimeRangeLabel, toDateTimePart, toDatePart } from '../../utils';

type Props = {
  schedule: PromotionSchedule;
};

const EMPTY = '-';

/**
 * id 링크 뒤에 오는 이름 — 링크와 붙어 `#4854형태가 가진 아름다움` 처럼 한 덩어리로 읽히는 걸 막는다.
 *
 * `StyledLinkedList` 안에만 두면 목록 밖(연결 기획전)에는 안 먹는다 — 실제로 그렇게 빠뜨렸었다.
 * 둘 다 쓰도록 바깥으로 뺀다.
 */
const StyledLinkedName = styled.span`
  margin-left: 8px;
`;

/**
 * 연결 목록 — **한 줄에 하나씩**.
 *
 * 쉼표로 이어 붙이면 개수가 늘수록 어디까지가 한 쿠폰인지 경계가 사라지고,
 * id 를 눌러 상세로 갈 수도 없다. 행으로 나눠 id·이름·혜택을 각자 자리에 둔다.
 */
const StyledLinkedList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;

  li + li {
    margin-top: 6px;
  }

  .benefit {
    margin-left: 8px;
    color: #6b7280;
  }
`;

const formatDiscount = (discountType: number, discountAmount: number): string =>
  discountType === COUPON_DISCOUNT_TYPE.RATE ? `${discountAmount}%` : `${discountAmount.toLocaleString()}원`;

/**
 * 상세 — 기본 정보 (읽기 전용) (AMZUWEB-682).
 *
 * 🔴 **여기에 폼을 다시 만들지 않는다.** 수정은 기존 `ScheduleFormModal` 을 그대로 연다 —
 * 그 모달 안에 폼·연결 UI·검증이 전부 들어 있어서, 상세에 입력 폼을 또 그리면
 * **같은 검증 로직이 두 벌**이 되고 한쪽만 고치는 날이 온다.
 *
 * 그래서 이 패널은 "지금 값이 무엇인가" 만 보여 주고 편집은 한 곳에 맡긴다.
 * (모달 = 빠른 수정 / 상세 = 전체를 한눈에, 라는 역할 분담이기도 하다.)
 */
export const ScheduleDetailPanel = ({ schedule }: Props): React.JSX.Element => {
  const timeRange = getTimeRangeLabel(schedule.startDate, schedule.endDate);

  return (
    <Descriptions bordered={true} column={2} size="small">
      <Descriptions.Item label="번호">{schedule.id}</Descriptions.Item>
      <Descriptions.Item label="트랙">
        <Tag color={TRACK_META[schedule.track]?.color}>{TRACK_META[schedule.track]?.label ?? schedule.track}</Tag>
      </Descriptions.Item>

      <Descriptions.Item label="항목명" span={2}>
        {schedule.title}
      </Descriptions.Item>

      <Descriptions.Item label="기간" span={2}>
        {toDatePart(schedule.startDate)} ~ {toDatePart(schedule.endDate)} (
        {getDayCount(schedule.startDate, schedule.endDate)}일){timeRange ? ` · ${timeRange}` : ''}
      </Descriptions.Item>

      <Descriptions.Item label="상태">{STATUS_LABEL[schedule.status]}</Descriptions.Item>
      <Descriptions.Item label="유형">{schedule.type ? TYPE_LABEL[schedule.type] : EMPTY}</Descriptions.Item>

      <Descriptions.Item label="담당자">{schedule.adminName ?? EMPTY}</Descriptions.Item>
      <Descriptions.Item label="연결 기획전">
        {/*
          `eventId` 는 남았는데 `event` 가 없으면 기획전이 지워진 것이다.
          비워 두면 "연결을 안 한 건" 과 구분이 안 되므로 id 라도 보여 준다 —
          다만 그때는 **링크를 걸지 않는다.** 없는 페이지로 보내면 사용자가 원인을 못 찾는다.
        */}
        {schedule.event ? (
          <>
            <OpenEventDetail text={`#${schedule.event.id}`} eventId={schedule.event.id} />
            <StyledLinkedName>{schedule.event.mainTitle}</StyledLinkedName>
          </>
        ) : schedule.eventId === null ? (
          EMPTY
        ) : (
          `#${schedule.eventId} — 정보를 불러오지 못했습니다(삭제되었을 수 있습니다)`
        )}
      </Descriptions.Item>

      <Descriptions.Item label="연결 쿠폰" span={2}>
        {schedule.coupons.length === 0 ? (
          EMPTY
        ) : (
          <StyledLinkedList>
            {schedule.coupons.map((coupon) => (
              <li key={coupon.id}>
                <OpenCouponDetail text={`#${coupon.id}`} couponId={coupon.id} />
                <StyledLinkedName>{coupon.name}</StyledLinkedName>
                <span className="benefit">{formatDiscount(coupon.discountType, coupon.discountAmount)}</span>
              </li>
            ))}
          </StyledLinkedList>
        )}
      </Descriptions.Item>

      <Descriptions.Item label="메모" span={2}>
        {schedule.memo ? <span style={{ whiteSpace: 'pre-wrap' }}>{schedule.memo}</span> : EMPTY}
      </Descriptions.Item>

      <Descriptions.Item label="등록일">{toDateTimePart(schedule.createDate)}</Descriptions.Item>
      <Descriptions.Item label="수정일">{toDateTimePart(schedule.updateDate)}</Descriptions.Item>
    </Descriptions>
  );
};
